import { getScrollTopToAlignElementTop as getScrollTopToAlignElementTopShared } from 'lib/dom/scrollGeometry'

/**
 * Pure geometry helpers for ChatGPT-style chat scroll.
 * Kept free of React so unit tests can cover edge cases without DOM hooks.
 */

export const NEAR_BOTTOM_THRESHOLD_PX = 32
export const OUT_OF_VIEW_THRESHOLD_PX = 116
/** Ignore tiny trackpad / layout noise when deciding the user scrolled away. */
export const USER_SCROLL_UP_DISENGAGE_PX = 48
/**
 * When progressive blank-absorb leaves only this much spacer, collapse the rest
 * (ChatGPT: clear fully once you scroll near the end of the blank).
 */
export const SPACER_ABSORB_REMAINDER_PX = 8

export type ScrollMetrics = {
  scrollTop: number
  clientHeight: number
  scrollHeight: number
  /** Trailing spacer height excluded from “real content” measurements. */
  spacerHeight: number
}

export function getContentScrollHeight(metrics: Pick<ScrollMetrics, 'scrollHeight' | 'spacerHeight'>): number {
  return Math.max(0, metrics.scrollHeight - metrics.spacerHeight)
}

/** Max scrollTop that keeps the viewport on real content (spacer excluded). */
export function getContentBottomScrollTop(metrics: ScrollMetrics): number {
  return Math.max(0, getContentScrollHeight(metrics) - metrics.clientHeight)
}

export function isNearContentBottom(
  metrics: ScrollMetrics,
  thresholdPx: number = NEAR_BOTTOM_THRESHOLD_PX,
): boolean {
  const distanceFromBottom = getContentScrollHeight(metrics) - (metrics.scrollTop + metrics.clientHeight)
  return distanceFromBottom <= thresholdPx
}

export function hasContentOutOfView(
  metrics: ScrollMetrics,
  thresholdPx: number = OUT_OF_VIEW_THRESHOLD_PX,
): boolean {
  const contentScrollHeight = getContentScrollHeight(metrics)
  if (contentScrollHeight <= metrics.clientHeight) {
    return false
  }
  const distanceFromBottom = contentScrollHeight - (metrics.scrollTop + metrics.clientHeight)
  return distanceFromBottom > thresholdPx
}

/**
 * Temporary trailing spacer so a just-sent user bubble can sit near the top
 * while the assistant reply grows into the empty space (ChatGPT pin layout).
 */
export function calculateStreamingPinSpacerHeight(args: {
  viewportHeight: number
  userMessageHeight: number
  assistantMessageHeight: number
  reservedSpacePx: number
}): number {
  const { viewportHeight, userMessageHeight, assistantMessageHeight, reservedSpacePx } = args
  return Math.max(0, viewportHeight - userMessageHeight - assistantMessageHeight - reservedSpacePx)
}

/**
 * scrollTop that aligns an element's top with the scroll container's top.
 * Prefer getBoundingClientRect deltas over element.offsetTop — messages are nested
 * (list wrappers / date separators), so offsetTop is often not container-relative
 * and re-pin lands mid-way.
 */
export function getScrollTopToAlignElementTop(args: {
  containerScrollTop: number
  containerTop: number
  elementTop: number
  /** Clearance to leave above the element (e.g. a sticky header's height). Defaults to 0. */
  offset?: number
}): number {
  return getScrollTopToAlignElementTopShared(args)
}

/**
 * Shrink pin blank by the same amount the reply grew.
 * Keeps total scrollHeight stable so the browser never clamps scrollTop
 * (which is what made the user bubble drift / flicker when we re-anchored).
 * Ignores non-positive deltas (layout jitter must not over-shrink).
 */
export function shrinkSpacerByContentGrowth(
  spacerHeight: number,
  contentHeightDeltaPx: number,
): number {
  if (spacerHeight <= 0 || contentHeightDeltaPx <= 0) {
    return Math.max(0, spacerHeight)
  }
  return Math.max(0, spacerHeight - contentHeightDeltaPx)
}

/**
 * True once the assistant reply has real streamed content (not placeholder / loader-only).
 * Pin spacer must not shrink for the Thinking loader alone.
 */
export function hasAssistantReplyContentStarted(message: {
  isPlaceholder?: boolean
  rawContent?: string
  parts: Array<{ type: string; content?: string; text?: string }>
}): boolean {
  if (message.isPlaceholder) {
    return false
  }
  if (message.rawContent?.trim()) {
    return true
  }
  return message.parts.some((part) => {
    if (part.type === 'text') {
      const text = part.content ?? part.text ?? ''
      return text.trim().length > 0
    }
    // Non-text parts (cards, carousels, etc.) count as stream content.
    return true
  })
}

/**
 * ChatGPT blank absorb: convert downward scroll into spacer shrinkage.
 * Returns how much spacer remains and how much of the scroll was absorbed.
 */
export function absorbScrollIntoSpacer(args: {
  spacerHeight: number
  scrollDelta: number
  remainderPx?: number
}): { nextSpacerHeight: number; absorbedDelta: number; didFullyClear: boolean } {
  const {
    spacerHeight,
    scrollDelta,
    remainderPx = SPACER_ABSORB_REMAINDER_PX,
  } = args

  if (spacerHeight <= 0 || scrollDelta <= 0) {
    return {
      nextSpacerHeight: Math.max(0, spacerHeight),
      absorbedDelta: 0,
      didFullyClear: spacerHeight <= 0,
    }
  }

  const absorbedDelta = Math.min(spacerHeight, scrollDelta)
  const nextSpacerHeight = spacerHeight - absorbedDelta
  const didFullyClear = nextSpacerHeight <= remainderPx

  return {
    nextSpacerHeight: didFullyClear ? 0 : nextSpacerHeight,
    absorbedDelta: didFullyClear ? spacerHeight : absorbedDelta,
    didFullyClear,
  }
}
